import { ApiError } from "./error";
import { fetchAuthSession } from "aws-amplify/auth";
import { savePostLoginRedirect } from "../features/login/postLoginRedirect";
import { scheduleMessagingMaintenanceRetry } from "./messagingMaintenanceRetry";

type Options = {
  auth: boolean;
  body?: object;
  headers?: Record<string, string>;
  queryParams?: Record<string, string>;
  requestId?: string;
};

type UploadOptions = {
  headers: Record<string, string>;
  body: Blob;
};

type Method = "GET" | "POST" | "PUT" | "DELETE";

export type Response<T> = {
  status: number;
  headers: Headers;
  url: string;
  data: T;
};

type TokenProvider = () => Promise<string | undefined>;

class ApiClient {
  private endpoint: string;
  private apiKey: string;
  private tokenProvider: TokenProvider;
  private redirectOnServiceUnavailable: boolean;

  constructor(
    endpoint: string,
    apiKey: string,
    tokenProvider: TokenProvider,
    redirectOnServiceUnavailable = true,
  ) {
    this.endpoint = endpoint;
    this.apiKey = apiKey;
    this.tokenProvider = tokenProvider;
    this.redirectOnServiceUnavailable = redirectOnServiceUnavailable;
  }

  // sanitize all control characters, see https://www.ascii-code.com/characters/control-characters
  // remove double quotes (brakes the openSearch queries)
  private sanitizeControlCharacters(value: string): string {
    return value.replace(/[\x00-\x1F\x7F|]/g, "");
  }

  private sanitizeQueryParams(
    params: Record<string, any>,
  ): Record<string, any> {
    return Object.entries(params).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]:
          value && typeof value === "string"
            ? this.sanitizeControlCharacters(value)
            : value,
      }),
      {},
    );
  }

  get = async <T>(path: string, options?: Options): Promise<Response<T>> => {
    const sanitizedQueryParams = options?.queryParams
      ? this.sanitizeQueryParams(options.queryParams)
      : undefined;

    path = sanitizedQueryParams
      ? `${path}?${this.encodeQueryParams(sanitizedQueryParams)}`
      : path;

    return this.send("GET", path, options);
  };

  getWithRequestId = async <T>(
    path: string,
    options?: Options,
  ): Promise<Response<T>> => {
    const sanitizedQueryParams = options?.queryParams
      ? this.sanitizeQueryParams(options.queryParams)
      : undefined;

    path = sanitizedQueryParams
      ? `${path}?${this.encodeQueryParams(sanitizedQueryParams)}`
      : path;

    return this.send("GET", path, this.withRequestId(options));
  };

  post = async <T>(path: string, options?: Options): Promise<Response<T>> =>
    this.send("POST", path, options);

  postWithRequestId = async <T>(
    path: string,
    options?: Options,
  ): Promise<Response<T>> => this.send("POST", path, this.withRequestId(options));

  put = async <T>(path: string, options?: Options): Promise<Response<T>> =>
    this.send("PUT", path, options);

  putWithRequestId = async <T>(
    path: string,
    options?: Options,
  ): Promise<Response<T>> => this.send("PUT", path, this.withRequestId(options));

  delete = async <T>(path: string, options?: Options): Promise<Response<T>> =>
    this.send("DELETE", path, options);

  deleteWithRequestId = async <T>(
    path: string,
    options?: Options,
  ): Promise<Response<T>> =>
    this.send("DELETE", path, this.withRequestId(options));

  upload = async <T>(
    path: string,
    options: UploadOptions,
  ): Promise<Response<T>> => this.sendUpload("PUT", path, options);

  private withRequestId = (options?: Options): Options => ({
    ...(options || { auth: false }),
    requestId: options?.requestId || crypto.randomUUID(),
  });

  private send = async (
    method: Method,
    path: string,
    options: Options = { auth: false },
  ) => {
    let data;
    try {
      const token = options.auth ? await this.tokenProvider() : undefined;
      const res = await fetch(`${this.endpoint}${path}`, {
        method,
        mode: "cors" as RequestMode,
        headers: {
          "X-Api-Key": this.apiKey,
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(options.requestId && { "X-Request-Id": options.requestId }),
          ...(options.body && { "Content-Type": "application/json" }),
          ...options.headers,
        },
        ...(options.body && { body: JSON.stringify(options.body) }),
      });

      data =
        Number(res.headers.get("content-length")) > 0 ? await res.json() : {};

      if (res.status === 503 && this.redirectOnServiceUnavailable) {
        let sections = window.location.href.split("/");
        window.location.href = sections[0] + "/maintenance";
      }

      if (res.status === 503 && !this.redirectOnServiceUnavailable) {
        const retryAfter = Number(res.headers.get("retry-after"));
        scheduleMessagingMaintenanceRetry(
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 180,
        );
      }

      if (!res.ok) {
        throw new ApiError(res.status, res.statusText);
      }

      if (data?.error) {
        throw new ApiError(res.status, data.error, data?.context);
      }

      return {
        status: res.status,
        headers: res.headers,
        url: res.url,
        data,
      };
    } catch (e: any) {
      if (e instanceof ApiError) throw e;
      throw new ApiError(500, e.message);
    }
  };

  private sendUpload = async (
    method: Method,
    path: string,
    options: UploadOptions,
  ) => {
    let data;
    try {
      const res = await fetch(`${path}`, {
        method,
        mode: "cors" as RequestMode,
        headers: {
          ...options.headers,
        },
        body: options.body,
      });

      data =
        Number(res.headers.get("content-length")) > 0 ? await res.json() : {};

      if (res.status === 503) {
        let sections = window.location.href.split("/");
        window.location.href = sections[0] + "/maintenance";
      }

      if (!res.ok) {
        throw new ApiError(res.status, res.statusText);
      }

      if (data?.error) {
        throw new ApiError(res.status, data.error);
      }

      return {
        status: res.status,
        headers: res.headers,
        url: res.url,
        data,
      };
    } catch (e: any) {
      if (e instanceof ApiError) throw e;
      throw new ApiError(500, e.message);
    }
  };

  private encodeQueryParams = (queryParams: Record<string, string>): string =>
    Object.entries(queryParams)
      .reduce(
        (a: string[], [k, v]) => [
          ...a,
          `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
        ],
        [],
      )
      .join("&");
}

const getTokenOrHandleSessionInvalid: () => Promise<
  string | undefined
> = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString();
  } catch (error) {
    console.error("Session is invalid or expired: ", error);

    setTimeout(() => {
      // Clear the user's session and redirect to the login page
      savePostLoginRedirect();
      localStorage.setItem("isLoggedIn", "false");
      window.location.href = "/login";
    }, 2000);

    throw new Error("sessionInvalidOrExpired");
  }
};

export const api = new ApiClient(
  process.env.REACT_APP_CORE_APIGW_ENDPOINT!,
  process.env.REACT_APP_CORE_APIGW_KEY!,
  () => getTokenOrHandleSessionInvalid(),
);

export const messageApi = new ApiClient(
  process.env.REACT_APP_CORE_MESSAGE_APIGW_ENDPOINT!,
  process.env.REACT_APP_CORE_MESSAGE_APIGW_KEY!,
  () => getTokenOrHandleSessionInvalid(),
  false,
);

export const eventApi = new ApiClient(
  `https://${process.env.REACT_APP_APPSYNC_HTTP_URL}/event`,
  process.env.REACT_APP_CORE_APIGW_KEY!,
  getTokenOrHandleSessionInvalid,
);
